import { defineStore } from "pinia";
import storage from "@/App/services/storage.service";
import { reactive, toRefs } from "vue";
import { getUserOptionsKey } from "@/App/options/getUserOptionsKey";

export enum CaseListType {
  Default,
  Compact,
  Map,
}

export enum CaseQuickFilterType {
  Fixed,
  Slide,
  None,
}

export type OptionsStore = {
  caseListType: CaseListType;
  pinQuickFilters: boolean;
  casesQuickFilterType: CaseQuickFilterType;
  showRefreshButton: boolean;
  showCaseMap: boolean;
  showCondensedCaseDetail: boolean;
  showDataIconLabels: boolean;
  showTimelineFilters: boolean;
  showUnacknowledgedQuickFilter: boolean;
  showTimers: boolean;
  groupCaseListByHospital: boolean;
  playSoundForCaseUpdates: number;
};

export const initialOptionsStore: OptionsStore = {
  caseListType: CaseListType.Default,
  pinQuickFilters: false,
  casesQuickFilterType: CaseQuickFilterType.None,
  showRefreshButton: false,
  showCaseMap: false,
  showCondensedCaseDetail: true,
  showDataIconLabels: true,
  showTimelineFilters: true,
  showUnacknowledgedQuickFilter: false,
  showTimers: true,
  groupCaseListByHospital: false,
  playSoundForCaseUpdates: 0,
};

const loadOptions = (): OptionsStore => {
  return {
    ...initialOptionsStore,
    ...storage.getItemAsObject(getUserOptionsKey()),
  };
};

export const useOptionsStore = defineStore("options", () => {
  const state = reactive(loadOptions());

  const setCasesListType = (type: CaseListType, forceSave: boolean = true): void => {
    state.caseListType = type;
    if (forceSave) saveOptions();
  };
  const setCasesQuickFilterType = (type: CaseQuickFilterType): void => {
    state.casesQuickFilterType = type;
    saveOptions();
  };

  const setUnacknowledgedQuickFilter = (show: boolean): void => {
    state.showUnacknowledgedQuickFilter = show;
    saveOptions();
  };

  const setRefreshVisibility = (show: boolean): void => {
    state.showRefreshButton = show;
    saveOptions();
  };

  const setCaseMapVisibility = (show: boolean): void => {
    state.showCaseMap = show;
    saveOptions();
  };
  const setCondensedCaseDetail = (show: boolean): void => {
    state.showCondensedCaseDetail = show;
    saveOptions();
  };
  const setDataIconLabels = (show: boolean): void => {
    state.showDataIconLabels = show;
    saveOptions();
  };
  const setTimelineFilters = (show: boolean): void => {
    state.showTimelineFilters = show;
    saveOptions();
  };

  const setTimers = (show: boolean): void => {
    state.showTimers = show;
    saveOptions();
  };
  const setHospitalGroupBy = (groupBy: boolean): void => {
    state.groupCaseListByHospital = groupBy;
    saveOptions();
  };

  const setPlaySoundForCaseUpdates = (play: number): void => {
    state.playSoundForCaseUpdates = play;
    saveOptions();
  };

  const setPinQuickFilters = (pin: boolean): void => {
    state.pinQuickFilters = pin;
    saveOptions();
  };
  const saveOptions = (): void => {
    storage.save(getUserOptionsKey(), state);
  };

  const loadState = (): void => {
    Object.assign(state, loadOptions());
  };

  const restoreDefaults = (): void => {
    Object.assign(state, initialOptionsStore);
    storage.removeItem(getUserOptionsKey());
  };

  return {
    ...toRefs(state),
    setCasesListType,
    setCasesQuickFilterType,
    setUnacknowledgedQuickFilter,
    setRefreshVisibility,
    setCaseMapVisibility,
    setCondensedCaseDetail,
    setDataIconLabels,
    setTimelineFilters,
    setTimers,
    setHospitalGroupBy,
    setPlaySoundForCaseUpdates,
    setPinQuickFilters,
    saveOptions,
    loadState,
    restoreDefaults,
  };
});
